import type { RigPart, RigSurface } from "@common/utils/operatorRig";

/** Live depth-buffered articulated art, not a directional sprite sheet.
 * A shared GPU atlas supplies body and weapon-overlap layers to the 2D game.
 */
export class OperatorRaster {
    static readonly cell = 192;
    static readonly extent = 6;
    static readonly capacity = 8;
    readonly canvas = document.createElement("canvas");
    private readonly gl: WebGLRenderingContext;
    private readonly mode: WebGLUniformLocation;
    private readonly buffer: WebGLBuffer;
    private readonly slots = new Set<number>();
    private vertices = new Float32Array(150000);
    private offset = 0;
    constructor(private readonly cell = OperatorRaster.cell) {
        this.canvas.width = this.canvas.height = cell * 4;
        const gl = this.canvas.getContext("webgl", { alpha: true, antialias: true, premultipliedAlpha: true, preserveDrawingBuffer: true });
        if (!gl) throw new Error("The arcade operator requires WebGL.");
        this.gl = gl;
        const compile = (type: number, source: string): WebGLShader => {
            const shader = gl.createShader(type);
            if (!shader) throw new Error("Could not allocate operator shader");
            gl.shaderSource(shader, source); gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "Operator shader failed");
            return shader;
        };
        const vertex = compile(gl.VERTEX_SHADER, `
            attribute vec3 aPosition;
            attribute vec3 aPigment;
            attribute vec2 aPaint;
            varying vec3 vPigment;
            varying vec2 vPaint;
            void main() {
                gl_Position = vec4(aPosition.x / 6.0, -aPosition.y / 6.0, -aPosition.z / 20.0, 1.0);
                vPigment = aPigment; vPaint = aPaint;
            }`);
        const fragment = compile(gl.FRAGMENT_SHADER, `
            precision mediump float;
            varying vec3 vPigment;
            varying vec2 vPaint;
            uniform float uForeground;
            void main() {
                if (uForeground > 0.5 && vPaint.y < 0.5) discard;
                float light = vPaint.x;
                float tone = light < -2.0 ? 1.0 :
                    0.60 + 0.24 * smoothstep(-0.20, 0.32, light) + 0.30 * smoothstep(0.38, 0.90, light);
                gl_FragColor = vec4(vPigment * tone, 1.0);
            }`);
        const program = gl.createProgram();
        if (!program) throw new Error("Could not allocate operator program");
        gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? "Operator program failed");
        gl.deleteShader(vertex); gl.deleteShader(fragment); gl.useProgram(program);
        const mode = gl.getUniformLocation(program, "uForeground"), buffer = gl.createBuffer();
        if (!mode || !buffer) throw new Error("Could not initialise operator renderer");
        this.mode = mode; this.buffer = buffer;
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        for (const [name, size, start] of [["aPosition", 3, 0], ["aPigment", 3, 3], ["aPaint", 2, 6]] as const) {
            const location = gl.getAttribLocation(program, name);
            gl.enableVertexAttribArray(location); gl.vertexAttribPointer(location, size, gl.FLOAT, false, 32, start * 4);
        }
        gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL); gl.enable(gl.SCISSOR_TEST);
        gl.clearColor(0, 0, 0, 0);
    }
    acquire(): number {
        for (let i = 0; i < OperatorRaster.capacity; i++) if (!this.slots.has(i)) { this.slots.add(i); return i; }
        throw new Error("All operator atlas slots are occupied");
    }
    release(slot: number): void { this.slots.delete(slot); }
    contextInfo(): { renderer: string; version: string; antialias: boolean | undefined } {
        const gl = this.gl, debug = gl.getExtension("WEBGL_debug_renderer_info");
        return { renderer: String(gl.getParameter(debug ? debug.UNMASKED_RENDERER_WEBGL : gl.RENDERER)),
            version: String(gl.getParameter(gl.VERSION)), antialias: gl.getContextAttributes()?.antialias };
    }
    region(slot: number, foreground = false): { x: number; y: number; width: number; height: number } {
        const cell = this.cell;
        return { x: slot % 4 * cell, y: (Math.floor(slot / 4) * 2 + (foreground ? 1 : 0)) * cell, width: cell, height: cell };
    }
    private vertex(x: number, y: number, z: number, color: number, light: number, front: number): void {
        if (this.offset + 8 > this.vertices.length) {
            const next = new Float32Array(this.vertices.length * 2); next.set(this.vertices); this.vertices = next;
        }
        this.vertices[this.offset++] = x; this.vertices[this.offset++] = y; this.vertices[this.offset++] = z;
        this.vertices[this.offset++] = (color >> 16 & 255) / 255;
        this.vertices[this.offset++] = (color >> 8 & 255) / 255;
        this.vertices[this.offset++] = (color & 255) / 255;
        this.vertices[this.offset++] = light; this.vertices[this.offset++] = front;
    }
    private addSurface(face: RigSurface, front: number): void {
        for (let i = 1; i < face.points.length - 1; i++) for (const j of [0, i, i + 1]) {
            const point = face.points[j];
            this.vertex(point.x, point.y, face.depths?.[j] ?? face.depth, face.pigment ?? face.color, face.lights?.[j] ?? 0.5, front);
        }
        if (!face.ink?.length) return;
        const [a, b, c] = face.points, depths = face.depths ?? face.points.map(() => face.depth);
        const determinant = (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
        if (Math.abs(determinant) < 1e-8) return;
        const dx = ((depths[1] - depths[0]) * (c.y - a.y) - (depths[2] - depths[0]) * (b.y - a.y)) / determinant;
        const dy = ((b.x - a.x) * (depths[2] - depths[0]) - (c.x - a.x) * (depths[1] - depths[0])) / determinant;
        for (const ink of face.ink) for (let i = 1; i < ink.length - 1; i++) for (const j of [0, i, i + 1]) {
            const point = ink[j];
            this.vertex(point.x, point.y, depths[0] + dx * (point.x - a.x) + dy * (point.y - a.y) + 0.001, 0x241c2a, -10, front);
        }
    }
    draw(parts: RigPart[], rotation: number, slot: number): void {
        this.offset = 0;
        for (const part of parts) {
            const front = part.name.startsWith("glove") || (Math.sin(rotation) < -0.15 && /^(helmet|visor|ear|crown|chin|face|neck|premiumHelmet|premiumCrest|sovereignCrown)/.test(part.name)) ? 1 : 0;
            for (const face of part.surfaces) this.addSurface(face, front);
        }
        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, this.vertices.subarray(0, this.offset), gl.DYNAMIC_DRAW);
        for (const foreground of [false, true]) {
            const rect = this.region(slot, foreground), y = this.canvas.height - rect.y - rect.height;
            gl.viewport(rect.x, y, rect.width, rect.height); gl.scissor(rect.x, y, rect.width, rect.height);
            gl.colorMask(true, true, true, true); gl.depthMask(true);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            gl.uniform1f(this.mode, 0);
            if (foreground) gl.colorMask(false, false, false, false);
            gl.drawArrays(gl.TRIANGLES, 0, this.offset / 8);
            if (foreground) {
                gl.colorMask(true, true, true, true); gl.uniform1f(this.mode, 1);
                gl.drawArrays(gl.TRIANGLES, 0, this.offset / 8);
            }
        }
    }
}
